// SkinExpert/src/app/layout/account/SubscriptionManagement.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import StripeService, { SubscriptionInfo } from "../../services/StripeService";
import { useSubscription } from "../../hooks/useSubscription";

// LIVE Stripe Price IDs
const SIMPLE_PRICE_ID = "price_1SsJnFEDUX6RH5PTd3Z8I6RG";
const ADVANCED_PRICE_ID = "price_1SsJnFEDUX6RH5PTIbZw55JG";
const PROFESSIONAL_PRICE_ID = "price_1SsJnFEDUX6RH5PTFYm0UkEb";

// TEST Stripe Price IDs (for development)
// const SIMPLE_PRICE_ID = "price_1Soj2aE0scdIJz7sehikkdwK";
// const ADVANCED_PRICE_ID = "price_1Sop2dE0scdIJz7szKZJPBh6";
// const PROFESSIONAL_PRICE_ID = "price_1Soj2aE0scdIJz7soqBQqm4h";

const plans = [
  {
    name: "Simple",
    priceId: SIMPLE_PRICE_ID,
    price: "€20",
    period: "/month",
    features: [
     "Mole mapping (early access – limited time)",
      "Early access - limited time to advanced AI analysis",
      "Core decision-support workflows",
      "Structured reporting & analytics",
      "2 GB secure medical image storage",
      "Standard support",
    ],
    tag: "EARLY ADOPTER",
    tagColor: "primary" as const,
  },
  {
    name: "Advanced",
    priceId: ADVANCED_PRICE_ID,
    price: "€80",
    period: "/month",
    features: [
      "Full access to advanced AI analysis",
      "Explainable AI (XAI) insights",
      "Mole mapping & longitudinal case tracking",
      "10 GB secure medical image storage",
      "Advanced reporting & analytics",
      "Priority support",
    ],
    recommended: true,
    tag: "MOST POPULAR",
    tagColor: "secondary" as const,
  },
  {
    name: "Professional",
    priceId: PROFESSIONAL_PRICE_ID,
    price: "€200",
    period: "/month",
    features: [
      "Advanced AI analysis with XAI",
      "Early access to new models and features",
      "Mole mapping & longitudinal evolution comparison",
      "Multi-user clinical workflows",
      "50 GB secure medical image storage",
      "Advanced reporting & exports",
      "Priority support",
    ],
    recommended: false,
    tag: "CLINIC",
    tagColor: "primary" as const,
  },
];

export default function SubscriptionManagement() {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);


    const {
      hasActiveSubscription,
      isAdvanced,
      isProfessional,
      hasSubscription,
      isAdmin,
      loading: loading2,
      refresh: refreshSubscription,
    } = useSubscription();
  

  useEffect(() => {
    loadSubscription();
    
    // Check if returning from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      // Auto-sync subscription after checkout
      handleSyncSubscription();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      console.log("Loading subscription...");
      const data = await StripeService.getSubscription();
      console.log("Subscription data received:", data);
      setSubscription(data);
    } catch (err: any) {
      console.error("Error loading subscription:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId: string) => {
    setError(null);
    setSuccess(null);
    setProcessingCheckout(true);

    try {
      const { url } = await StripeService.createCheckoutSession(
        priceId,
        window.location.origin + "/account?session_id={CHECKOUT_SESSION_ID}",
        window.location.origin + "/account?canceled=true"
      );
      window.location.href = url;
    } catch (err: any) {
      setError(err.message);
      setProcessingCheckout(false);
    }
  };

  const handleCancelSubscription = async () => {
    setError(null);
    setSuccess(null);

    try {
      const result = await StripeService.cancelSubscription();
      setSuccess(result.message);
      setCancelDialogOpen(false);
      await loadSubscription();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSyncSubscription = async () => {
    setError(null);
    setSuccess(null);

    try {
      const result = await StripeService.syncSubscription();
      setSuccess("Subscription synced successfully! Refreshing...");
      await loadSubscription();
      // Refresh user claims to get updated subscription in token
      await refreshSubscription();
      setSuccess("Subscription synced and claims refreshed!");
    } catch (err: any) {
      // Silently fail if no subscription to sync
      console.error("Sync error:", err);
    }
  };

  const handleManageBilling = async () => {
    try {
      const { url } = await StripeService.createCustomerPortalSession(
        window.location.origin + "/account"
      );
      window.location.href = url;
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      maxWidth="1400px" 
      mx="auto" 
      mt={{ xs: 2, sm: 3, md: 4 }}
      px={{ xs: 1.5, sm: 2 }}
      sx={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
        borderRadius: { xs: 2, sm: 3 },
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box textAlign="center" mb={{ xs: 4, sm: 5, md: 6 }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{ 
            fontWeight: 700,
            fontSize: 'clamp(1.75rem, 5vw, 3rem)',
            lineHeight: 1.2,
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Subscription Management
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mt: 1,
            fontSize: 'clamp(0.875rem, 2.5vw, 1.25rem)',
            lineHeight: 1.5,
            px: { xs: 2, sm: 0 },
          }}
        >
            {subscription?.hasSubscription
              ? "Manage your current plan or upgrade to a new one"
              : "Choose the perfect plan for your needs"}
          {/* Choose the perfect plan for your needs */}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Current Subscription Status */}
      {subscription?.hasSubscription && (
        <Card 
          sx={{ 
            mb: { xs: 3, sm: 4 },
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
              }}
            >
              Current Subscription
            </Typography>
            <Box 
              display="flex" 
              alignItems="center" 
              gap={{ xs: 1, sm: 2 }} 
              mb={2}
              sx={{ 
                flexWrap: 'wrap',
                '& .MuiChip-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  height: { xs: 28, sm: 32 },
                }
              }}
            >
              <Chip
                label={subscription.planType?.toUpperCase()}
                color="primary"
                icon={<StarIcon />}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={subscription.status}
                color={subscription.status === "active" ? "success" : "warning"}
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {subscription.currentPeriodEnd &&
                `Renews on: ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}
            </Typography>
            {subscription.cancelAtPeriodEnd && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Your subscription will be canceled at the end of the billing period
              </Alert>
            )}
            <Box 
              mt={2} 
              display="flex" 
              gap={{ xs: 1.5, sm: 2 }}
              sx={{ 
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                '& > button': {
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  width: { xs: '100%', sm: 'auto' },
                }
              }}
            >
              {isAdmin && (
              <Button variant="outlined" onClick={handleManageBilling}>
                Manage Billing
              </Button>)}
              {!subscription.cancelAtPeriodEnd && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  Cancel Subscription
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={handleSyncSubscription}
                sx={{ ml: { xs: 0, sm: 1 } }}
              >
                Sync Subscription
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}


      {/* Subscription Plans */}
{isAdmin && (      
      <Typography 
        variant="h4" 
        gutterBottom 
        mb={{ xs: 3, sm: 4 }}
        textAlign="center"
        sx={{ 
          fontWeight: 600,
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
        }}
      >
        {subscription?.hasSubscription ? "Upgrade Your Plan" : "Choose Your Plan"}
      </Typography>
)}


      {isAdmin && (
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.priceId}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                border: plan.recommended ? "2px solid" : "1px solid",
                borderColor: plan.recommended ? "primary.main" : "divider",
                borderRadius: 3,
                transition: 'all 0.3s ease-in-out',
                boxShadow: plan.recommended 
                  ? '0 8px 40px rgba(99, 102, 241, 0.25)' 
                  : '0 4px 20px rgba(0,0,0,0.08)',
                transform: plan.recommended ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: plan.recommended 
                    ? '0 12px 50px rgba(99, 102, 241, 0.35)' 
                    : '0 8px 30px rgba(0,0,0,0.15)',
                },
              }}
            >
              {plan.tag && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    pt: 2,
                  }}
                >
                  <Chip
                    label={plan.tag}
                    color={plan.tagColor}
                    size="small"
                    icon={plan.recommended ? <StarIcon /> : <RocketLaunchIcon />}
                    sx={{ 
                      fontWeight: 700,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1, p: 4, pt: plan.tag ? 6 : 4 }}>
                <Typography 
                  variant="h5" 
                  component="div" 
                  gutterBottom
                  sx={{ fontWeight: 700, mb: 3 }}
                >
                  {plan.name}
                </Typography>
                <Box display="flex" alignItems="baseline" mb={4}>
                  <Typography 
                    variant="h3" 
                    component="span"
                    sx={{ 
                      fontWeight: 800,
                      background: plan.recommended 
                        ? 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)'
                        : 'inherit',
                      backgroundClip: plan.recommended ? 'text' : 'inherit',
                      WebkitBackgroundClip: plan.recommended ? 'text' : 'inherit',
                      WebkitTextFillColor: plan.recommended ? 'transparent' : 'inherit',
                    }}
                  >
                    {plan.price}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
                    {plan.period}
                  </Typography>
                </Box>
                <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0 }}>
                  {plan.features.map((feature, index) => (
                    <Box
                      component="li"
                      key={index}
                      display="flex"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <CheckCircleIcon
                        color="primary"
                        sx={{ mr: 1.5, fontSize: 20, mt: 0.3, flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
              <Box p={4} pt={0}>
                <Button
                  fullWidth
                  variant={plan.recommended ? "contained" : "outlined"}
                  size="large"
                  onClick={() => handleSubscribe(plan.priceId)}
                  disabled={
                    processingCheckout ||
                    (subscription?.hasSubscription &&
                      subscription.planType === plan.name.toLowerCase())
                  }
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderRadius: 2,
                    ...(plan.recommended && {
                      background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                      },
                    }),
                  }}
                >
                  {subscription?.hasSubscription &&
                  subscription.planType === plan.name.toLowerCase()
                    ? "Current Plan"
                    : `Subscribe to ${plan.name}`}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>)}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel your subscription? You will continue to have
            access until the end of your current billing period.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Subscription</Button>
          <Button onClick={handleCancelSubscription} color="error" variant="contained">
            Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
